import { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MessageSquare, CheckSquare, Calendar, Users, Clock, 
  ArrowRight, Plus, Bell, TrendingUp, AlertTriangle, Search, Zap
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import UrgentNotificationModal from '../../components/notifications/UrgentNotificationModal';

import { API_URL } from '../../config/api';

export default function ClientDashboard() {
  const { user, isAssistant, assignedClients } = useOutletContext();
  const [stats, setStats] = useState({
    unreadMessages: 0,
    pendingTasks: 0,
    todayEvents: 0,
    assistants: 0,
    clients: 0
  });
  const [recentTasks, setRecentTasks] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [opportunitiesCount, setOpportunitiesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showUrgentModal, setShowUrgentModal] = useState(false);
  const [selectedClientForNotif, setSelectedClientForNotif] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      // Fetch conversations
      const convResponse = await fetch(`${API_URL}/api/client/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const conversations = convResponse.ok ? await convResponse.json() : [];
      
      // Fetch tasks
      const tasksResponse = await fetch(`${API_URL}/api/client/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const tasks = tasksResponse.ok ? await tasksResponse.json() : [];

      // Fetch calendar events
      const eventsResponse = await fetch(`${API_URL}/api/client/calendar/events`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const events = eventsResponse.ok ? await eventsResponse.json() : [];

      // Fetch assistants (only for clients)
      let assistantsList = [];
      if (!isAssistant) {
        const assistantsResponse = await fetch(`${API_URL}/api/client/users/${user.id}/assistants`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        assistantsList = assistantsResponse.ok ? await assistantsResponse.json() : [];
      }

      // Fetch public opportunities (for artisans/pros)
      let opportunitiesList = [];
      try {
        const oppResponse = await fetch(`${API_URL}/api/artisans/public-devis-requests`);
        if (oppResponse.ok) {
          const oppData = await oppResponse.json();
          opportunitiesList = oppData.devis_requests || [];
        }
      } catch (e) {
        console.log('Could not fetch opportunities');
      }

      // Calculate stats
      const unreadMessages = conversations.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
      const pendingTasks = tasks.filter(t => t.status !== 'done').length;
      const today = new Date().toDateString();
      const todayEvents = events.filter(e => new Date(e.start_time).toDateString() === today).length;

      setStats({
        unreadMessages,
        pendingTasks,
        todayEvents,
        assistants: assistantsList.length,
        clients: assignedClients?.length || 0
      });

      setRecentTasks(tasks.slice(0, 5));
      setRecentMessages(conversations.slice(0, 3));
      setAssistants(assistantsList);
      setOpportunities(opportunitiesList.slice(0, 3));
      setOpportunitiesCount(opportunitiesList.length);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  // Dynamic stat cards based on role
  const statCards = isAssistant ? [
    { icon: MessageSquare, label: 'Messages non lus', value: stats.unreadMessages, color: 'text-blue-500', bg: 'bg-blue-50', link: '/espace-client/messages' },
    { icon: CheckSquare, label: 'Tâches en cours', value: stats.pendingTasks, color: 'text-amber-500', bg: 'bg-amber-50', link: '/espace-client/tasks' },
    { icon: Calendar, label: "Événements aujourd'hui", value: stats.todayEvents, color: 'text-green-500', bg: 'bg-green-50', link: '/espace-client/calendar' },
    { icon: Users, label: 'Clients gérés', value: stats.clients, color: 'text-purple-500', bg: 'bg-purple-50', link: null },
  ] : [
    { icon: MessageSquare, label: 'Messages non lus', value: stats.unreadMessages, color: 'text-blue-500', bg: 'bg-blue-50', link: '/espace-client/messages' },
    { icon: CheckSquare, label: 'Tâches en cours', value: stats.pendingTasks, color: 'text-amber-500', bg: 'bg-amber-50', link: '/espace-client/tasks' },
    { icon: Calendar, label: "Événements aujourd'hui", value: stats.todayEvents, color: 'text-green-500', bg: 'bg-green-50', link: '/espace-client/calendar' },
    { icon: Users, label: 'Assistants dédiés', value: stats.assistants, color: 'text-purple-500', bg: 'bg-purple-50', link: '/espace-client/team' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'todo': return 'bg-slate-100 text-slate-600';
      case 'in_progress': return 'bg-blue-100 text-blue-600';
      case 'done': return 'bg-green-100 text-green-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'todo': return 'À faire';
      case 'in_progress': return 'En cours';
      case 'done': return 'Terminée';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-rm-primary to-rm-primary/80 rounded-2xl p-6 text-white">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">
          {getGreeting()}, {user.name} !
        </h1>
        <p className="text-white/80">
          Bienvenue dans ton espace Cockpit Pro. Gérez tes tâches, communiquez avec ton équipe et suivez ton activité.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Link
              to={stat.link}
              className="block bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-slate-100"
            >
              <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center mb-3`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <p className="text-2xl font-bold text-rm-text">{stat.value}</p>
              <p className="text-sm text-rm-text-secondary">{stat.label}</p>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Opportunities Banner - Visible prominently */}
      {opportunitiesCount > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-5 text-white shadow-lg"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Search className="w-7 h-7" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold">Nouvelles opportunités</h3>
                  <span className="bg-white text-orange-600 text-sm font-bold px-2.5 py-0.5 rounded-full animate-pulse">
                    {opportunitiesCount}
                  </span>
                </div>
                <p className="text-white/90 text-sm mt-1">
                  Des clients recherchent un artisan comme toi. Propose ton devis !
                </p>
              </div>
            </div>
            <Link to="/espace-client/crm/devis">
              <Button className="bg-white text-orange-600 hover:bg-orange-50 font-semibold shadow-md" data-testid="view-opportunities-btn">
                <Zap className="w-4 h-4 mr-2" />
                Voir les demandes
              </Button>
            </Link>
          </div>
          
          {/* Preview of latest opportunities */}
          {opportunities.length > 0 && (
            <div className="mt-4 grid md:grid-cols-3 gap-3">
              {opportunities.map((opp, idx) => (
                <div key={opp.id || idx} className="bg-white/15 backdrop-blur-sm rounded-lg p-3">
                  <p className="font-semibold truncate">{opp.prestation_name || opp.project_type || 'Projet'}</p>
                  <p className="text-sm text-white/80">{opp.city} ({opp.postal_code?.slice(0,2)})</p>
                  {opp.urgency === 'urgent' && (
                    <span className="inline-block mt-1 text-xs bg-red-500 px-2 py-0.5 rounded">Urgent</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Tasks */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-rm-text flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-rm-primary" />
              Tâches récentes
            </h2>
            <Link to="/espace-client/tasks">
              <Button variant="ghost" size="sm" className="text-rm-primary">
                Voir tout <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="p-4 space-y-3">
            {recentTasks.length === 0 ? (
              <div className="text-center py-8">
                <CheckSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-rm-text-secondary">Aucune tâche pour le moment</p>
                <Link to="/espace-client/tasks">
                  <Button size="sm" className="mt-3 bg-rm-primary">
                    <Plus className="w-4 h-4 mr-1" /> Créer une tâche
                  </Button>
                </Link>
              </div>
            ) : (
              recentTasks.map((task) => (
                <div key={task.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-rm-text truncate">{task.title}</p>
                    {task.assigned_to_name && (
                      <p className="text-xs text-rm-text-secondary">→ {task.assigned_to_name}</p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(task.status)}`}>
                    {getStatusLabel(task.status)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Team / Assistants or Clients for assistants */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-rm-text flex items-center gap-2">
              <Users className="w-5 h-5 text-rm-primary" />
              {isAssistant ? 'Mes clients' : 'Mon équipe'}
            </h2>
            {!isAssistant && (
              <Link to="/espace-client/team">
                <Button variant="ghost" size="sm" className="text-rm-primary">
                  Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            )}
          </div>
          <div className="p-4 space-y-3">
            {isAssistant ? (
              // Show assigned clients for assistants
              assignedClients?.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-rm-text-secondary">Aucun client assigné</p>
                </div>
              ) : (
                assignedClients?.map((client) => (
                  <div key={client.id} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-semibold">
                        {client.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-rm-text">{client.name}</p>
                      <p className="text-xs text-rm-text-secondary">{client.company || client.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedClientForNotif(client);
                        setShowUrgentModal(true);
                      }}
                      className="p-2 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
                      title="Envoyer une alerte urgente"
                      data-testid={`urgent-btn-${client.id}`}
                    >
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                ))
              )
            ) : (
              // Show assistants for clients
              assistants.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-rm-text-secondary">Aucun assistant assigné</p>
                  <p className="text-xs text-slate-400 mt-1">Contacte notre équipe RH pour obtenir ton assistant</p>
                </div>
              ) : (
                assistants.map((assistant) => (
                  <div key={assistant.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-10 h-10 bg-rm-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-rm-primary font-semibold">
                        {assistant.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-rm-text">{assistant.name}</p>
                      <p className="text-xs text-rm-text-secondary">{assistant.email}</p>
                    </div>
                    <Link to="/espace-client/messages">
                      <Button size="sm" variant="outline">
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                ))
              )
            )}
          </div>
        </div>
      </div>

      {/* Urgent Notification Modal for Assistants */}
      {showUrgentModal && selectedClientForNotif && (
        <UrgentNotificationModal
          clientId={selectedClientForNotif.id}
          clientName={selectedClientForNotif.name}
          onClose={() => {
            setShowUrgentModal(false);
            setSelectedClientForNotif(null);
          }}
        />
      )}

      {/* Recent Conversations */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-rm-text flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-rm-primary" />
            Conversations récentes
          </h2>
          <Link to="/espace-client/messages">
            <Button variant="ghost" size="sm" className="text-rm-primary">
              Voir tout <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
        <div className="p-4">
          {recentMessages.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-rm-text-secondary">Aucune conversation</p>
              <p className="text-xs text-slate-400 mt-1">Commence à discuter avec ton équipe</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentMessages.map((conv) => (
                <Link
                  key={conv.id}
                  to={`/espace-client/messages?conversation=${conv.id}`}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="w-10 h-10 bg-rm-primary/10 rounded-full flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-rm-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-rm-text truncate">{conv.title}</p>
                    {conv.last_message && (
                      <p className="text-xs text-rm-text-secondary truncate">{conv.last_message}</p>
                    )}
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="bg-rm-secondary text-white text-xs px-2 py-0.5 rounded-full">
                      {conv.unread_count}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
